import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { type GridApi } from 'ag-grid-community';

import { AG_SOQL_EXPORT_QUERY_LIMIT } from 'common/visualizations/helpers/TableDataExportHelpers';
import { type ColumnAggregation } from 'common/visualizations/dataProviders/types';
import {
  type ExportData,
  type TableColumnFormat,
  type HeaderFormatStyle,
  type TableExportType,
} from 'common/visualizations/views/agGridReact/types';
import { type RowStripeStyle } from 'common/types/agGrid/rowStripe';
import {
  type CustomSortConfig,
  type OrderConfig,
  type LinkedAction,
  type Hierarchy,
} from 'common/visualizations/vif';
import { AgGridDataProcessor } from '../processors/GridDataProcessor';
import {
  type ExtraOptions,
  type Dataset,
  type PreviewSettings,
  type ReportMetadata,
  type WorkerOutputMessage,
} from '../types';
import type { TableActionsConfigs } from 'common/visualizations/views/agGridReact/TableActionsButton/types';
import { PDFPreview } from './PdfPreview';
import { Settings } from './Settings';
import { usePdfWorker } from './usePdfWorker';
import { PRINT_PREVIEW_FONT_SLIDER_CONFIG } from '../templates/section/defaultStyles';
import {
  isTableAdvancedTotalingForExportsEnabled,
  isTablePrintingImprovementTextSizeSliderEnabled,
  isTablePrintingImprovementForFiltersEnabled,
  isTablePrintingImprovementFormattingEnabled,
  isTablePrintingImprovementGroupExpansionEnabled,
  isTablePrintingImprovementHideDuplicateGroupingsEnabled,
  isTablePrintingImprovementGroupingLayoutEnabled,
  isTablePrintingImprovementHideGridlinesEnabled,
  isTablePrintingImprovementHideElementsEnabled,
  isTablePrintingImprovementInheritGroupExpansionEnabled,
} from 'common/visualizations/helpers/VifSelectors';
import { getFiltersWithDisplayNames } from '../templates/section/helper';
import { formatNumberFilterValuesForDisplay } from './formatNumberFilterValuesForDisplay';
import { FILTER_FUNCTION } from 'common/components/FilterBar/SoqlFilter';
import { getReportingPeriodDisplayText } from '../processors/filterHumanReadableConverter';

interface PreviewPaneProps {
  columnFormats: { [key: string]: TableColumnFormat };
  dataset: Dataset;
  getExportData: (
    selectedFiltered: boolean,
    exportType: TableExportType,
    rowLimit?: number,
  ) => Promise<ExportData>;
  gridApi: GridApi;
  nonStandardAggregations: ColumnAggregation[] | null;
  onError?: (error: string) => void;
  reportMetadata: ReportMetadata;
  showSubTotal: boolean;
  showTotal: boolean;
  hideDuplicateGroups?: boolean;
  vifOrderConfig: OrderConfig[];
  columnSortConfig?: CustomSortConfig;
  tableActionsContext?: TableActionsConfigs;
  linkedAction?: LinkedAction;
  hierarchyConfig?: Hierarchy;
  rowStripeStyle?: RowStripeStyle;
  headerFormat?: HeaderFormatStyle;
  expandedGroupsMap?: Record<string, boolean>;
  agGridOpenNodeLevel?: number;
  isIndented?: boolean;
}

export type PREVIEW_STATUS =
  | 'initializing'
  | 'loading'
  | 'working'
  | 'tooManyRows'
  | 'error'
  | 'done'
  | 'limitExceededError';

const defaultExpansionSettings = (
  shouldShowGroupExpansionSetting: boolean,
): 'inherit' | 'expanded' | undefined => {
  if (shouldShowGroupExpansionSetting && isTablePrintingImprovementInheritGroupExpansionEnabled()) {
    return 'inherit';
  }
  if (shouldShowGroupExpansionSetting && !isTablePrintingImprovementInheritGroupExpansionEnabled()) {
    return 'expanded';
  }
  return undefined;
};

const getDefaultSettings = (gridApi: GridApi): PreviewSettings => {
  const pivotMode = gridApi.isPivotMode();
  const rowGroupCount = gridApi.getRowGroupColumns().length;
  // For ordinary tables, there needs at least one row group for group expansion setting to be visible.
  // For pivot tables, it needs at least two.
  const minimumRowGroupCountForGroupExpansion = pivotMode ? 2 : 1;
  const shouldShowGroupExpansionSetting =
    isTablePrintingImprovementGroupExpansionEnabled() &&
    rowGroupCount >= minimumRowGroupCountForGroupExpansion;

  return {
    title: '',
    pageSize: 'Letter',
    orientation: 'landscape',
    scale: 1,
    monetaryUnit: 'thousands',
    pageBreak: 'standard',
    textOverflow: 'linebreak',
    fontSize: PRINT_PREVIEW_FONT_SLIDER_CONFIG.DEFAULT,
    template: rowGroupCount > 0 ? 'grouped-aggregate' : 'transaction-table',
    // when the text size slider flag is on, default to 'inherit' so the slider renders on
    // first open (Settings only shows the slider when columnWidth === 'inherit'). otherwise
    // keep the legacy 'bestfit' default.
    columnWidth: isTablePrintingImprovementTextSizeSliderEnabled() ? 'inherit' : 'bestfit',
    groupExpansion: defaultExpansionSettings(shouldShowGroupExpansionSetting),
    showGridlines: true,
    metadataVisibility: isTablePrintingImprovementHideElementsEnabled() ? 'all' : undefined,
    showLogo: true,
  };
};

const dataProcessor = new AgGridDataProcessor();

/**
 * Determines if the row count exceeds the PDF export limit.
 *
 * When group expansion is enabled, checks the actual rowData length (visible rows after
 * filters and group expansion/collapse). Otherwise, uses processedRowCount (total rows
 * before group expansion).
 */
const exceedsRowLimit = (rowDataLength: number, processedRowCount: number): boolean => {
  if (isTablePrintingImprovementGroupExpansionEnabled()) {
    return rowDataLength > AG_SOQL_EXPORT_QUERY_LIMIT;
  }
  return processedRowCount > AG_SOQL_EXPORT_QUERY_LIMIT;
};

export const PreviewPane = ({
  columnFormats,
  dataset,
  getExportData,
  gridApi,
  nonStandardAggregations,
  onError,
  reportMetadata,
  showSubTotal,
  showTotal,
  hideDuplicateGroups,
  vifOrderConfig,
  columnSortConfig,
  tableActionsContext,
  linkedAction,
  hierarchyConfig,
  rowStripeStyle,
  headerFormat,
  expandedGroupsMap,
  agGridOpenNodeLevel,
  isIndented,
}: PreviewPaneProps) => {
  const [settings, setSettings] = useState<PreviewSettings>({ ...getDefaultSettings(gridApi) });
  const currentBlobUrl = useRef<string | null>(null);
  const [pdfData, setPdfData] = useState<string | null>(null);
  const [status, setStatus] = useState<PREVIEW_STATUS>('initializing');
  const [currentRowCount, setCurrentRowCount] = useState<number>(0);
  const isAdvancedTotalingForExportsEnabled = isTableAdvancedTotalingForExportsEnabled();

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (currentBlobUrl.current) {
        URL.revokeObjectURL(currentBlobUrl.current);
        currentBlobUrl.current = null;
      }
    };
  }, []);

  // Generate reporting period value from the selected filter
  const reportingPeriodValue = useMemo(() => {
    if (!settings.reportingPeriod || !reportMetadata.filters) {
      return undefined;
    }

    // Find the filter that matches the selected reporting period
    const matchingFilter = reportMetadata.filters.find((filter) =>
      filter.columns.some((column) => column.fieldName === settings.reportingPeriod),
    );

    if (!matchingFilter) {
      return undefined;
    }

    const getFallbackDisplayName = () =>
      matchingFilter.displayName ||
      matchingFilter.columns[0]?.displayName ||
      matchingFilter.columns[0]?.fieldName ||
      undefined;

    // For non-date filter types, return fallback display name
    if (
      matchingFilter.function !== FILTER_FUNCTION.TIME_RANGE &&
      matchingFilter.function !== FILTER_FUNCTION.RELATIVE_DATE_RANGE
    ) {
      return getFallbackDisplayName();
    }

    try {
      return getReportingPeriodDisplayText(matchingFilter);
    } catch (error) {
      console.error('Error getting reporting period display text:', error, {
        filterFunction: matchingFilter.function,
        filterArguments: matchingFilter.arguments,
      });
      return getFallbackDisplayName();
    }
  }, [settings.reportingPeriod, reportMetadata.filters]);

  // Create enhanced reportMetadata with reportingPeriodValue
  const enhancedReportMetadata = useMemo(() => {
    if (!isTablePrintingImprovementForFiltersEnabled()) {
      return null;
    }

    const filtersWithDisplayNames = getFiltersWithDisplayNames(
      reportMetadata.filters,
      columnFormats,
      reportMetadata.columns,
    );
    // pre-format number filter values here (main thread) so the worker, which has no
    // `window`, can render them WYSIWYG with the filter bar (e.g. currency -> $200.00).
    const filtersWithFormattedValues = formatNumberFilterValuesForDisplay(
      filtersWithDisplayNames,
      reportMetadata.columns,
    );
    return {
      ...reportMetadata,
      filters: filtersWithFormattedValues,
      reportingPeriodValue,
    };
  }, [reportMetadata, reportingPeriodValue, columnFormats]);

  const onMessage = useCallback(
    (message: MessageEvent<WorkerOutputMessage>) => {
      switch (message.data.type) {
        case 'preview': {
          // Cleanup previous blob before creating new one
          if (currentBlobUrl.current) {
            URL.revokeObjectURL(currentBlobUrl.current);
          }
          const blobUrl = URL.createObjectURL(message.data.data.content);
          currentBlobUrl.current = blobUrl;
          setPdfData(blobUrl);
          setStatus('done');
          break;
        }
        case 'error':
        case 'limitExceededError':
          setStatus(message.data.type);
          break;
      }
    },
    [setStatus],
  );
  const worker = usePdfWorker({ onError, onMessage, settings, rowCount: currentRowCount });
  useEffect(() => {
    if (!worker) {
      return;
    }
    setStatus('loading');
    const extraOptions: ExtraOptions = {
      columnFormats,
      columnSortConfig,
      dataset,
      featureFlags: {
        isTableAdvancedTotalingForExportsEnabled: isTableAdvancedTotalingForExportsEnabled(),
        isTablePrintingImprovementForFiltersEnabled: isTablePrintingImprovementForFiltersEnabled(),
        isTablePrintingImprovementFormattingEnabled: isTablePrintingImprovementFormattingEnabled(),
        isTablePrintingImprovementGroupingLayoutEnabled: isTablePrintingImprovementGroupingLayoutEnabled(),
        isTablePrintingImprovementHideDuplicateGroupingsEnabled:
          isTablePrintingImprovementHideDuplicateGroupingsEnabled() && hideDuplicateGroups,
        isTablePrintingImprovementHideElementsEnabled: isTablePrintingImprovementHideElementsEnabled(),
        isTablePrintingImprovementHideGridlinesEnabled: isTablePrintingImprovementHideGridlinesEnabled(),
        isTablePrintingImprovementInheritGroupExpansionEnabled:
          isTablePrintingImprovementInheritGroupExpansionEnabled(),
      },
      groupExpansion: settings.groupExpansion,
      headerFormat: isTablePrintingImprovementFormattingEnabled() ? headerFormat : undefined,
      hideDuplicateGroups,
      hierarchyConfig,
      isIndented,
      linkedAction,
      metadataVisibility: settings.metadataVisibility,
      nonStandardAggregations,
      rowStripeStyle: isTablePrintingImprovementFormattingEnabled() ? rowStripeStyle : undefined,
      showSubTotal,
      showTotal,
      tableActionsContext,
      expandedGroupsMap,
      groupColumnIds: isTablePrintingImprovementInheritGroupExpansionEnabled()
        ? gridApi.getRowGroupColumns().map((col) => col.getColId())
        : undefined,
      agGridOpenNodeLevel,
      vifOrderConfig,
    };
    // fetch one extra row so we can tell "exactly at the limit" apart from "truncated at the limit"
    const getExportDataWithSentinel = (selectedFiltered: boolean) =>
      getExportData(selectedFiltered, 'PDF', AG_SOQL_EXPORT_QUERY_LIMIT + 1);
    dataProcessor
      .processGridData(gridApi, getExportDataWithSentinel, extraOptions)
      .then((processedData) => {
        if (exceedsRowLimit(processedData.rowData.length, processedData.processedRowCount)) {
          setStatus('tooManyRows');
          return;
        }

        if (worker) {
          setStatus('working');
          setCurrentRowCount(processedData.rowData.length);
          worker.postMessage({
            type: 'generatePreview',
            data: processedData,
            settings,
            reportMetadata: isTablePrintingImprovementForFiltersEnabled()
              ? enhancedReportMetadata
              : reportMetadata,
            extraOptions,
          });
        } else {
          setStatus('error');
          console.error('Worker is no longer available');
        }
      })
      .catch((error) => {
        setStatus('error');
        console.error('Error processing grid data:', error);
      });
  }, [
    columnFormats,
    dataset,
    getExportData,
    gridApi,
    nonStandardAggregations,
    enhancedReportMetadata,
    settings,
    showSubTotal,
    showTotal,
    hideDuplicateGroups,
    vifOrderConfig,
    columnSortConfig,
    tableActionsContext,
    linkedAction,
    worker,
    hierarchyConfig,
    isAdvancedTotalingForExportsEnabled,
    rowStripeStyle,
    reportMetadata,
    headerFormat,
  ]);

  return (
    <>
      <Settings
        settings={settings}
        onChange={setSettings}
        filters={isTablePrintingImprovementForFiltersEnabled() ? enhancedReportMetadata?.filters : undefined}
      />
      <PDFPreview status={status} pdfData={pdfData} />
    </>
  );
};
